import { currentUserId } from "./auth";
import { kanbanOn } from "./kanbanLink";
import { boardMeta, createOwnedTask, loadCrmWeek, notifyWeekChange } from "./week";

export type TaskProperty = {
  key: string;
  label: string;
  values: Array<{ key: string; label: string; color?: string; text?: string }>;
};

export type TaskBadge = { key: string; label: string; bg: string; text: string };

const PROP_SESSION = "crm-task-props-v1";
let propMemory: TaskProperty[] | null = null;

export function peekProperties(): TaskProperty[] | null {
  if (propMemory) return propMemory;
  try {
    const raw = sessionStorage.getItem(PROP_SESSION);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as TaskProperty[];
    if (!Array.isArray(parsed)) return null;
    propMemory = parsed;
    return parsed;
  } catch {
    return null;
  }
}

function rememberProperties(rows: TaskProperty[]): TaskProperty[] {
  propMemory = rows;
  try {
    sessionStorage.setItem(PROP_SESSION, JSON.stringify(rows));
  } catch {
    /* quota pleine */
  }
  return rows;
}

export async function listProperties(): Promise<TaskProperty[]> {
  if (!kanbanOn()) {
    const board = await loadCrmWeek();
    return rememberProperties(mapProperties(board.properties, new Map()));
  }
  const userId = currentUserId();
  if (!userId) return [];
  const [templatesRes, colorsRes] = await Promise.all([
    fetch(`/api_kanban/templates/get_templates.php?user_id=${encodeURIComponent(userId)}`, { credentials: "include", cache: "no-store" }),
    fetch("/api_kanban/templates/get_user_colors.php", {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ user_id: userId }),
    }),
  ]);
  const templates = (await templatesRes.json().catch(() => ({}))) as { ok?: boolean; data?: { properties?: RawProperty[] } };
  const colors = (await colorsRes.json().catch(() => ({}))) as { ok?: boolean; colors?: Array<{ prop_key?: string; color?: string }> };
  const tint = new Map((colors.ok && colors.colors ? colors.colors : []).map((row) => [String(row.prop_key || ""), String(row.color || "")]));
  const rows = templates.ok && templates.data?.properties ? templates.data.properties : [];
  return rememberProperties(mapProperties(rows, tint));
}

function mapProperties(rows: RawProperty[], tint: Map<string, string>): TaskProperty[] {
  return rows
    .filter((prop) => prop.type !== "text" && Array.isArray(prop.values) && prop.values.length > 0)
    .filter((prop) => prop.key !== "statut" && prop.key !== "note_labels")
    .map((prop) => ({
      key: prop.key,
      label: prop.label || prop.key,
      values: prop.values.map((value) => {
        const custom = tint.get(value.key);
        const bg = custom || value.color?.bg || "#e5e7eb";
        return {
          key: value.key,
          label: value.label || value.key,
          color: bg,
          text: custom ? readableText(bg) : (value.color?.text || readableText(bg)),
        };
      }),
    }))
    .sort((a, b) => a.label.localeCompare(b.label, "fr"));
}

export async function createTask(input: { title: string; day: string; time?: string; allDay?: boolean; props?: Record<string, string>; fiche?: { id: number; name: string }; description?: string; board?: string; boardProps?: Array<{ label: string; value: string; color: string }> }): Promise<void> {
  if (!kanbanOn()) {
    await createOwnedTask(input);
    return;
  }
  const userId = currentUserId();
  if (!userId) throw new Error("Session introuvable.");
  const allDay = input.allDay !== false && !input.time;
  const clock = (input.time || "09:00").slice(0, 5);
  const start = allDay ? `${input.day} 09:00:00` : `${input.day} ${clock}:00`;
  const res = await fetch("/api_kanban/kanban/save-task.php", {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      id: `task_${Math.random().toString(16).slice(2, 14)}`,
      title: input.title.slice(0, 180) || "Note",
      description: input.description || "",
      status: "todo",
      position: 0,
      start,
      allDay: allDay ? 1 : 0,
      view_id: `default-kanban-${userId}`,
      user_id: userId,
      client_name: input.fiche?.name || "",
      meta: { allDay: allDay ? 1 : 0, ...(input.props || {}), ...(input.fiche ? { crmFicheId: input.fiche.id } : {}), ...boardMeta(input) },
    }),
  });
  const data = (await res.json().catch(() => ({}))) as { ok?: boolean; error?: string };
  if (!res.ok || data.ok === false) {
    throw new Error(data.error || "Mes tâches ne répond pas.");
  }
  notifyWeekChange();
}

export function taskBadges(meta: Record<string, unknown>, catalog: TaskProperty[]): TaskBadge[] {
  const ordered = [...catalog].sort((a, b) => Number(b.key === "importance") - Number(a.key === "importance"));
  const badges: TaskBadge[] = [];
  ordered.forEach((prop) => {
    if (prop.key === "client" || prop.key === "assignedTo") return;
    const raw = meta[prop.key];
    if (raw === null || raw === undefined || raw === "" || raw === false) return;
    const value = prop.values.find((item) => item.key === String(raw));
    if (!value) return;
    badges.push({ key: prop.key, label: value.label, bg: value.color || "#e5e7eb", text: value.text || readableText(value.color || "#e5e7eb") });
  });
  return badges;
}

export function importanceColor(meta: Record<string, unknown>, catalog: TaskProperty[]): string {
  return taskBadges(meta, catalog).find((badge) => badge.key === "importance")?.bg || "";
}

function readableText(hex: string): string {
  const color = hex.replace("#", "");
  if (color.length < 6) return "#111827";
  const red = Number.parseInt(color.slice(0, 2), 16);
  const green = Number.parseInt(color.slice(2, 4), 16);
  const blue = Number.parseInt(color.slice(4, 6), 16);
  if ([red, green, blue].some((part) => Number.isNaN(part))) return "#111827";
  return (red * 299 + green * 587 + blue * 114) / 1000 >= 128 ? "#000" : "#fff";
}

type RawProperty = {
  key: string;
  label?: string;
  type?: string;
  values: Array<{ key: string; label?: string; color?: { bg?: string; text?: string } }>;
};
